/**
 * Featured Artist Content Database
 *
 * Only entries with actual files in public/audio/ or public/videos/ are listed.
 *
 * Video fields:
 * - videoUrl: Direct URL to self-hosted MP4
 * - thumbnailUrl: Thumbnail image URL
 */

import { CONTENT_FLAGS } from '../config/contentFlags';
import { FEATURED_ARTISTS, LESSONS, PACKS } from './contentStore';
import { isPackUnlocked } from './entitlements';

// NOTE S7 Phase A : la donnee des artistes vit dans la base (source unique). Le runtime
// lit FEATURED_ARTISTS depuis le miroir (contentStore). Les fonctions ci-dessous operent
// sur l'objet vif.
export { FEATURED_ARTISTS };


const isArtistVisible = (artist) => CONTENT_FLAGS.showPaidArtists || artist.price === 'Free';

// Visibilité éditoriale, même règle pour les leçons et les backings : seul le
// contenu 'published' est affiché dans l'app. new/progress/ready = en cours de
// curation, unpublished = retiré sans suppression.
// Le staging web (beta.upscale.li) est buildé avec REACT_APP_SHOW_READY_LESSONS=true :
// le contenu 'ready' y est visible avant sa publication en prod.
const SHOW_READY_LESSONS = process.env.REACT_APP_SHOW_READY_LESSONS === 'true';
export const isContentVisible = (item) =>
  item.status === 'published' || (SHOW_READY_LESSONS && item.status === 'ready');

/**
 * Get all artists that have content for a specific scale
 */
export const getArtistsForScale = (scaleName) => {
  if (!CONTENT_FLAGS.showArtistVideos) return [];
  const results = [];

  for (const [artistId, artist] of Object.entries(FEATURED_ARTISTS)) {
    if (!isArtistVisible(artist)) continue;
    if (artist.videosByScale && artist.videosByScale[scaleName]) {
      results.push({
        artist,
        video: artist.videosByScale[scaleName]
      });
    }
  }

  return results;
};

/**
 * Get a featured artist for a scale (picks best match)
 */
export const getFeaturedArtistForScale = (scaleName) => {
  if (!CONTENT_FLAGS.showArtistVideos) return null;
  const artists = getArtistsForScale(scaleName);

  if (artists.length === 0) {
    // Return first artist with videos as fallback with generic content
    const artistsWithVideos = Object.values(FEATURED_ARTISTS).filter(a => isArtistVisible(a) && a.videosByScale);
    if (artistsWithVideos.length === 0) return null;
    const artist = artistsWithVideos[0];
    return {
      artist,
      video: {
        id: 'generic',
        title: `${scaleName} Improvisation`,
        description: `Watch ${artist.name} demonstrate creative approaches`,
        duration: '10:00',
        previewDuration: '0:30',
        isGeneric: true
      }
    };
  }

  return artists[0];
};

/**
 * Get ALL backing tracks sorted by relevance to the current scale context.
 * Priority: 1) perfect match (mode + root), 2) compatible scale (from MIDI analysis), 3) everything else
 */
export const getAllBackingTracksSorted = (currentMode, rootNote) => {
  const perfect = [];
  const compatible = [];
  const sameScale = [];
  const other = [];

  for (const artist of Object.values(FEATURED_ARTISTS)) {
    if (!isArtistVisible(artist)) continue;
    if (!artist.backingTracksByScale) continue;
    for (const [scaleName, tracks] of Object.entries(artist.backingTracksByScale)) {
      for (const track of tracks) {
        if (!isContentVisible(track)) continue;
        const entry = { artist, track, scaleName };
        if (scaleName === currentMode && track.rootNote === rootNote) {
          entry.relevance = 'perfect';
          perfect.push(entry);
        } else if (
          track.rootNote === rootNote &&
          track.compatibleScales &&
          track.compatibleScales.includes(currentMode)
        ) {
          // compatibleScales are bare mode names anchored to the track's own root,
          // so a compatible match requires the same root as well (E Minor Pentatonic
          // fits an E backing, A Minor Pentatonic does not).
          entry.relevance = 'compatible';
          compatible.push(entry);
        } else if (scaleName === currentMode) {
          // Same mode, different root (e.g. D Ionian for a C Ionian user). Not jammable
          // as-is, but tapping transposes the user to the track's key — same flavour.
          entry.relevance = 'sameScale';
          sameScale.push(entry);
        } else {
          entry.relevance = 'other';
          other.push(entry);
        }
      }
    }
  }

  return [...perfect, ...compatible, ...sameScale, ...other];
};

/**
 * Get ALL videos across all artists (only real ones with videoUrl)
 */
export const getAllVideos = () => {
  if (!CONTENT_FLAGS.showArtistVideos) return [];
  const results = [];
  for (const artist of Object.values(FEATURED_ARTISTS)) {
    if (!isArtistVisible(artist)) continue;
    if (!artist.videosByScale) continue;
    for (const [scaleName, video] of Object.entries(artist.videosByScale)) {
      if (video.videoUrl) {
        results.push({ artist, video, scaleName });
      }
    }
  }
  return results;
};

/**
 * Get ALL backing tracks across all artists (only real ones with audioUrl)
 */
export const getAllBackingTracks = () => {
  const results = [];
  for (const artist of Object.values(FEATURED_ARTISTS)) {
    if (!isArtistVisible(artist)) continue;
    if (!artist.backingTracksByScale) continue;
    for (const [scaleName, tracks] of Object.entries(artist.backingTracksByScale)) {
      for (const track of tracks) {
        if (track.audioUrl && isContentVisible(track)) {
          results.push({ artist, track, scaleName });
        }
      }
    }
  }
  return results;
};

/**
 * Toutes les lecons Guitar Pro du miroir, artiste joint.
 * Triees par position puis par titre. Seules les lecons visibles remontent, comme les
 * backings. Les lignes supprimees sont deja filtrees par buildLessons ; une lecon dont l'artiste n'existe pas (donnee incoherente ou artiste
 * retire) est ignoree, elle n'aurait rien a afficher sur sa card.
 */
export const getAllLessons = () => {
  const results = [];
  for (const lesson of Object.values(LESSONS)) {
    if (!isContentVisible(lesson)) continue;
    const artist = FEATURED_ARTISTS[lesson.artistId];
    if (!artist) continue;
    results.push({ lesson, artist });
  }
  return results.sort((a, b) => {
    const byPosition = (a.lesson.position ?? 0) - (b.lesson.position ?? 0);
    if (byPosition !== 0) return byPosition;
    return (a.lesson.title || '').localeCompare(b.lesson.title || '');
  });
};

/**
 * Lecons classees par pertinence pour le contexte de gamme courant :
 * 1) celles ecrites exactement dans la gamme courante (meme mode ET meme fondamentale),
 * 2) le reste.
 * Dans chaque groupe, l'ordre du pack (position) est garde.
 */
export const getLessonsForScale = (currentMode, rootNote) => {
  const exact = [];
  const other = [];

  for (const entry of getAllLessons()) {
    const { lesson } = entry;
    if (lesson.mode === currentMode && lesson.rootNote === rootNote) exact.push(entry);
    else other.push(entry);
  }

  return [...exact, ...other];
};

// Un pack sans rien de visible n'a pas de carte : un pack de lecons 'ready' existe
// en prod pour le staging, il ne doit pas apparaitre vide sur upscale.li/app.
const packHasVisibleContent = (packId) =>
  getTracksForPack(packId).length > 0 || getAllLessons().some(({ lesson }) => lesson.packId === packId);

/**
 * Packs de backing tracks (l'achat se fait par pack).
 */
export const getAllPacks = () =>
  Object.values(PACKS)
    .filter((p) => {
      const artist = FEATURED_ARTISTS[p.artistId];
      return artist && isArtistVisible(artist) && packHasVisibleContent(p.id);
    })
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

export const getPackById = (packId) => (packId ? PACKS[packId] || null : null);

// Tous les backings visibles d'un pack (pour la page de detail du pack).
export const getTracksForPack = (packId) => {
  const results = [];
  for (const artist of Object.values(FEATURED_ARTISTS)) {
    if (!isArtistVisible(artist) || !artist.backingTracksByScale) continue;
    for (const tracks of Object.values(artist.backingTracksByScale)) {
      for (const track of tracks) {
        if (track.packId === packId && isContentVisible(track)) {
          results.push({ artist, track });
        }
      }
    }
  }
  return results;
};

// Promo active = un prix promo ET une date de fin encore a venir.
export const isPackPromoActive = (pack) =>
  !!(pack?.promoPrice && pack?.promoUntil && new Date(pack.promoUntil) > new Date());

// Un backing est jouable si :
// - il est offert individuellement (track.free : echantillon vitrine) ;
// - il n'appartient a aucun pack ;
// - son pack est deverrouille (cf. isPackUnlocked : bundled -> app achetee/Patreon,
//   IAP -> achat du pack, ou pack beta_included pour un testeur beta actif).
export const isTrackUnlocked = (track, purchasedPackIds) => {
  if (track.free) return true;
  const pack = getPackById(track.packId);
  if (!pack) return true;
  const purchased = !!purchasedPackIds && purchasedPackIds.has(pack.id);
  return isPackUnlocked(pack, purchased);
};

export default FEATURED_ARTISTS;
